import { useMemo, useState } from "react";
import Input from "./Input";
import { Colors } from "../utils/colors";
import { Fonts } from "../utils/fonts";
import API from "../services";
import type { IShareDataArg, IPublicationsItem } from "../services/types";
import { useToast } from "./ToastProvider";
import { FontSizes } from "../assets/fonts/fontSizes";

type Props = {
  open: boolean;
  onClose: () => void;
  publication: IPublicationsItem;
};

export default function ShareEmailModal({ open, onClose, publication }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phonenumber, setPhonenumber] = useState("");
  const [company, setCompany] = useState("");
  const [designation, setDesignation] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const toast = useToast();

  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const isValidPhoneNumber = (val: string) => /^[0-9+]+$/.test(val);

  const canSubmit = useMemo(() => {
    return (
      name?.trim() &&
      company?.trim() &&
      designation?.trim() &&
      email?.trim() &&
      phonenumber?.trim()
    );
  }, [name, company, designation, email, phonenumber]);

  const clearState = () => {
    setName("");
    setEmail("");
    setPhonenumber("");
    setCompany("");
    setDesignation("");
  };

  const handleShare = async () => {
    if (!email || !isValidEmail(email)) {
      toast.info("Invalid email address");
      return;
    }
    if (!phonenumber || !isValidPhoneNumber(phonenumber)) {
      toast.info("Invalid phone number");
      return;
    }
    if (!company || !name || !designation) {
      toast.info("Please fill in all fields");
      return;
    }

    const params: IShareDataArg = {
      publicationGuid: publication.publicationGuid,
      sharedDocName:name,
      sharedEmail:email,
      phoneNumber: phonenumber,
      companyName: company,
      designation,
    };

    try {
      setSubmitting(true);
      const status = await API.shareDocument(params);
      console.log("Share document status:", status);
      if (status) {
        console.log("Document shared successfully");
        toast.success("Article shared successfully");
        onClose();
        clearState();
      } else {
        toast.error("Something went wrong!!!");
      }
    } catch (err) {
      toast.error("Something went wrong!!!");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div style={styles.overlay} onClick={onClose} />
      <div style={styles.modal} role="dialog" aria-modal="true">
        <div style={styles.title}>Share via email</div>

        <Input placeholder="Name" value={name} onChangeText={setName} />
        <Input
          placeholder="Email"
          value={email}
          onChangeText={setEmail}
          keyboardType="email-address"
        />
        <Input
          placeholder="Phone Number"
          value={phonenumber}
          onChangeText={setPhonenumber}
          keyboardType="numeric"
        />
        <Input
          placeholder="Company"
          value={company}
          onChangeText={setCompany}
        />
        <Input
          placeholder="Designation"
          value={designation}
          onChangeText={setDesignation}
        />

        <div style={styles.row}>
          <button type="button" onClick={onClose} style={styles.cancelButton}>
            <span style={styles.cancel}>Cancel</span>
          </button>

          <button
            type="button"
            onClick={handleShare}
            style={{
              ...styles.button,
              opacity: !canSubmit || submitting ? 0.6 : 1,
              cursor: !canSubmit || submitting ? "not-allowed" : "pointer",
            }}
            disabled={!canSubmit || submitting}
          >
            <span style={styles.submit}>
              {submitting ? "Submitting..." : "Submit"}
            </span>
          </button>
        </div>
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.30)",
    zIndex: 9997,
  },
  modal: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: Colors.White,
    borderRadius: 20,
    padding: 20,
    zIndex: 9998,
    width: 360,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  title: {
    fontSize: FontSizes.xxxl,
    fontWeight: Fonts.SemiBold,
    color: Colors.Black,
    paddingBottom: 20,
    fontFamily: "Inter",
  },
  row: {
    paddingTop: 10,
    width: "100%",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  button: {
    height: 40,
    width: 140,
    backgroundColor: Colors.SecondaryColor,
    border: "none",
    borderRadius: 10,
  },
  submit: {
    color: Colors.White,
    fontWeight: Fonts.SemiBold,
    fontFamily: "Inter",
  },
  cancelButton: {
    height: 40,
    width: 140,
    background: "transparent",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
  },
  cancel: {
    color: Colors.SecondaryColor,
    fontWeight: Fonts.SemiBold,
    fontFamily: "Inter",
  },
};
